import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'

function normalizeItem(item) {
  if (!item || typeof item !== 'object') {
    return null
  }

  if (typeof item.title === 'string' && typeof item.content === 'string') {
    return {
      title: item.title.trim() || 'Untitled',
      content: item.content.trim(),
      type: item.type || 'note',
      source: item.source || 'ai_suggested'
    }
  }

  if (typeof item.content === 'string' && !item.title) {
    const lines = item.content.split('\n').map(l => l.trim()).filter(Boolean)
    return {
      title: lines[0] || 'Untitled',
      content: lines.slice(1).join('\n') || item.content,
      type: item.type || 'note',
      source: 'ai_suggested'
    }
  }

  return null
}

function WikiItemSuggestions({ items, selectedItems, onToggle }) {
  const normalized = (items || [])
    .map(normalizeItem)
    .filter(Boolean)

  if (!normalized || normalized.length === 0) {
    return null
  }

  const getTypeColor = (type) => {
    const colors = {
      link: 'bg-blue-100 text-blue-800',
      address: 'bg-green-100 text-green-800',
      phone: 'bg-purple-100 text-purple-800',
      note: 'bg-gray-100 text-gray-800',
      document: 'bg-amber-100 text-amber-800',
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  return (
    <Card>
      <div className="mb-4">
        <h3 className="font-heading text-lg font-bold mb-1">
          Wiki Item Suggestions
        </h3>
        <p className="text-sm text-brand-slate">
          AI found {normalized.length} {normalized.length === 1 ? 'item' : 'items'} to add to your team wiki. 
          Select which ones to keep.
        </p>
      </div>

      <div className="space-y-3">
        {normalized.map((item, index) => (
          <div
            key={index}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              selectedItems.includes(index)
                ? 'border-brand-green bg-green-50'
                : 'border-border bg-white hover:border-brand-green/50'
            }`}
            onClick={() => onToggle(index)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-brand-charcoal">
                    {item.title}
                  </h4>
                  <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(item.type)}`}>
                    {item.type}
                  </span>
                  {item.source === 'ai_suggested' && (
                    <Badge variant="secondary">AI</Badge>
                  )}
                </div>
                <p className="text-sm text-brand-slate break-all">
                  {item.content}
                </p>
                {item.requires_privacy_review && (
                  <p className="text-xs text-amber-700 mt-2">
                    Contains personal data. Review carefully before publishing.
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                <div 
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                    selectedItems.includes(index)
                      ? 'bg-brand-green border-brand-green'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedItems.includes(index) && (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>{selectedItems.length} of {normalized.length} items selected.</strong>
          {' '}These will be added to your team wiki for easy reference.
        </p>
      </div>
    </Card>
  )
}

export default WikiItemSuggestions

